import { randomUUID } from "#/shared/utils/string";

// Used to send an analytics event from only one page when several pages hold a copy of it. A tab the browser duplicates
// starts with a copy of the original's outbox, and a same-origin frame shares its parent's. A page claims events as it
// sends them, and skips the events another page has claimed or it has sent itself. Claims are kept in `localStorage`,
// which every tab on the origin shares, under one key per event. A claim holds the random id of the page that claimed
// it and the time it was claimed, and nothing about the visitor or what they did. A claim is kept for `maxAge`, as long
// as a copy of its event can still be sent.
//
// Checking and recording a claim are two steps, so two pages claiming the same event at the same moment can both send
// it.
//
// Decision record: https://github.com/combinaut/sparkle/wiki/ADR-0029-Batch-Analytics-Events-in-Our-Own-Modules-Around-a-Vendored-Ahoy-JS
export class AnalyticsEventClaims {
  // @param options {Object}
  //   - `key`: the prefix of the `localStorage` keys the claims are kept under, each followed by `:` and an event id.
  //     Defaults to `sparkle_analytics_claims`.
  //   - `maxAge`: the milliseconds a claim is kept. Defaults to the outbox's default `maxAge`.
  constructor(options) {
    options = options || {};

    this.prefix = (options.key || "sparkle_analytics_claims") + ":";
    this.maxAge = options.maxAge || 4 * 60 * 60 * 1000;
    this.page = randomUUID();
    this.sentIds = new Set();
  }

  // Returns the claims for the events `outbox` holds, kept under its prefix followed by `claims`.
  static forOutbox(outbox) {
    return new AnalyticsEventClaims({ key: outbox.prefix + "claims" });
  }

  // Returns the events that no other page has claimed and this page has not sent, and claims them for this page. An
  // event without an `id` is always returned. Returns every event this page has not sent when the browser denies
  // `localStorage`.
  claim(events) {
    const now = Date.now() / 1000;

    return events.filter((event) => {
      if (!event.id) {
        return true;
      }

      if (this.sentIds.has(event.id)) {
        return false;
      }

      if (this.claimedElsewhere(this.read(event.id))) {
        return false;
      }

      this.write(event.id, [this.page, now]);

      return true;
    });
  }

  // Records that this page has sent the events, so `claim` leaves them out on this page until they are released. A
  // page's own stored claim does not stop it from claiming the event again.
  sent(events) {
    events.forEach((event) => {
      if (event.id) {
        this.sentIds.add(event.id);
      }
    });
  }

  // Drops this page's claims on the events, and the record that it sent them, so this page or another may send them.
  release(events) {
    events.forEach((event) => {
      if (!event.id) {
        return;
      }

      this.sentIds.delete(event.id);

      const claim = this.read(event.id);

      if (claim && claim[0] === this.page) {
        this.remove(event.id);
      }
    });
  }

  // Removes the claims past `maxAge`, and those that cannot be read. Does nothing when the browser denies
  // `localStorage`.
  purge() {
    let keys;

    try {
      keys = Object.keys(localStorage).filter((key) => key.startsWith(this.prefix));
    } catch {
      return;
    }

    keys.forEach((key) => {
      const id = key.slice(this.prefix.length);

      if (!this.current(this.read(id))) {
        this.remove(id);
      }
    });
  }

  // Returns the claim on the event as `[pageId, secondsSinceEpoch]`, or null when there is none, it cannot be read, or
  // the browser denies `localStorage`.
  read(id) {
    try {
      const claim = JSON.parse(localStorage.getItem(this.prefix + id));

      return Array.isArray(claim) ? claim : null;
    } catch {
      return null;
    }
  }

  write(id, claim) {
    try {
      localStorage.setItem(this.prefix + id, JSON.stringify(claim));
    } catch {
      // Without a stored claim, a copy of the event may be sent twice.
    }
  }

  remove(id) {
    try {
      localStorage.removeItem(this.prefix + id);
    } catch {
      // A claim that cannot be removed expires after `maxAge`.
    }
  }

  // Returns whether the claim is another page's and within `maxAge`.
  claimedElsewhere(claim) {
    return this.current(claim) && claim[0] !== this.page;
  }

  current(claim) {
    return Array.isArray(claim) && claim[1] > (Date.now() - this.maxAge) / 1000;
  }
}
